import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, fmtPrice } from "../api.js";

interface Service {
  id: string;
  name: string;
  description: string | null;
  durationMin: number;
  priceFrom: number;
  priceTo: number | null;
  categoryId: string | null;
  isActive: boolean;
  sortOrder: number;
}

interface ServiceCategory {
  id: string;
  name: string;
  sortOrder: number;
}

const EMPTY_FORM = {
  name: "",
  description: "",
  durationMin: 60,
  priceFrom: 0,
  priceTo: "",
  categoryId: "",
  isActive: true,
};

export function ServicesPage({ token }: { token: string }) {
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState("");
  const [showPriceSectionControls, setShowPriceSectionControls] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [editCategoryId, setEditCategoryId] = useState<string | null>(null);
  const [editCategoryName, setEditCategoryName] = useState("");

  const { data: services = [], isLoading } = useQuery({
    queryKey: ["services"],
    queryFn: () => apiFetch<Service[]>(token, "/api/services"),
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["service-categories"],
    queryFn: () => apiFetch<ServiceCategory[]>(token, "/api/services/categories"),
  });

  const createMutation = useMutation({
    mutationFn: (data: object) =>
      apiFetch(token, "/api/services", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["services"] });
      setShowForm(false);
      setForm(EMPTY_FORM);
      setError("");
    },
    onError: (e: Error) => setError(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: object }) =>
      apiFetch(token, `/api/services/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["services"] });
      setEditId(null);
      setShowForm(false);
      setForm(EMPTY_FORM);
      setError("");
    },
    onError: (e: Error) => setError(e.message),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiFetch(token, `/api/services/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ isActive }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["services"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch(token, `/api/services/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["services"] });
      setError("");
    },
    onError: (e: Error) => setError(e.message),
  });

  const createCategoryMutation = useMutation({
    mutationFn: (name: string) =>
      apiFetch(token, "/api/services/categories", {
        method: "POST",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["service-categories"] });
      setNewCategoryName("");
      setShowPriceSectionControls(true);
      setError("");
    },
    onError: (e: Error) => setError(e.message),
  });

  const updateCategoryMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiFetch(token, `/api/services/categories/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["service-categories"] });
      setEditCategoryId(null);
      setEditCategoryName("");
      setError("");
    },
    onError: (e: Error) => setError(e.message),
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch(token, `/api/services/categories/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["service-categories"] });
      void qc.invalidateQueries({ queryKey: ["services"] });
      setError("");
    },
    onError: (e: Error) => setError(e.message),
  });

  function openCreate() {
    setEditId(null);
    setForm(EMPTY_FORM);
    setError("");
    setShowForm(true);
  }

  function openEdit(s: Service) {
    setEditId(s.id);
    setForm({
      name: s.name,
      description: s.description ?? "",
      durationMin: s.durationMin,
      priceFrom: s.priceFrom,
      priceTo: s.priceTo != null ? String(s.priceTo) : "",
      categoryId: s.categoryId ?? "",
      isActive: s.isActive,
    });
    setError("");
    setShowForm(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      name: form.name,
      description: form.description || undefined,
      durationMin: Number(form.durationMin),
      priceFrom: Number(form.priceFrom),
      ...(form.priceTo ? { priceTo: Number(form.priceTo) } : {}),
      ...(showPriceSectionControls || categories.length > 0 ? { categoryId: form.categoryId || null } : {}),
      isActive: form.isActive,
    };
    if (editId) {
      updateMutation.mutate({ id: editId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function handleCreateCategory(e: React.FormEvent) {
    e.preventDefault();
    const name = newCategoryName.trim();
    if (!name) return;
    createCategoryMutation.mutate(name);
  }

  function startEditCategory(category: ServiceCategory) {
    setEditCategoryId(category.id);
    setEditCategoryName(category.name);
  }

  function saveEditedCategory(categoryId: string) {
    const name = editCategoryName.trim();
    if (!name) return;
    updateCategoryMutation.mutate({ id: categoryId, name });
  }

  if (isLoading) {
    return <div className="p-4 text-center text-gray-400 py-10">Загрузка...</div>;
  }

  const active = services.filter((s) => s.isActive);
  const inactive = services.filter((s) => !s.isActive);
  const categoryById = new Map(categories.map((category) => [category.id, category]));
  const categoryServiceCounts = new Map<string, number>();
  for (const service of services) {
    if (!service.categoryId) continue;
    categoryServiceCounts.set(service.categoryId, (categoryServiceCounts.get(service.categoryId) ?? 0) + 1);
  }
  const hasConfiguredPriceSections = categories.length > 0;
  const shouldSuggestPriceSections = active.length > 5 && !hasConfiguredPriceSections;
  const shouldShowPriceSectionBlock =
    showPriceSectionControls || hasConfiguredPriceSections || shouldSuggestPriceSections;

  return (
    <div className="mx-auto w-full max-w-2xl min-w-0 p-4">
      <div className="mb-5 flex min-w-0 items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-gray-800">Услуги</h2>
          <p className="text-sm text-gray-400">{active.length} активных</p>
        </div>
        <button
          onClick={openCreate}
          className="shrink-0 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
        >
          + Добавить
        </button>
      </div>

      {error && !showForm && (
        <p className="mb-4 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
      )}

      {/* Форма */}
      {shouldShowPriceSectionBlock && (
        <section className="mb-5 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
          <div className="flex min-w-0 items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-gray-800">Разделы прайса</h3>
              <p className="mt-1 text-sm leading-5 text-gray-500">
                Если услуг больше пяти или у вас несколько направлений, разделите прайс: например
                "Маникюр", "Педикюр", "Брови". На сайте появятся компактные фильтры, а без разделов
                прайс останется обычным списком.
              </p>
            </div>
            {!showPriceSectionControls && (
              <button
                type="button"
                onClick={() => setShowPriceSectionControls(true)}
                className="shrink-0 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 transition hover:bg-gray-50"
              >
                Разделить прайс
              </button>
            )}
          </div>

          {(showPriceSectionControls || hasConfiguredPriceSections) && (
            <div className="mt-4 space-y-3">
              <form onSubmit={handleCreateCategory} className="flex min-w-0 gap-2">
                <input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  className="min-w-0 flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm"
                  placeholder="Новый раздел: Маникюр, Педикюр..."
                />
                <button
                  type="submit"
                  disabled={createCategoryMutation.isPending || !newCategoryName.trim()}
                  className="shrink-0 rounded-xl bg-gray-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-gray-800 disabled:opacity-50"
                >
                  Добавить
                </button>
              </form>

              {categories.length > 0 && (
                <div className="space-y-2">
                  {categories.map((category) => (
                    <div
                      key={category.id}
                      className="flex min-w-0 items-center gap-2 rounded-xl bg-gray-50 px-3 py-2"
                    >
                      {editCategoryId === category.id ? (
                        <input
                          value={editCategoryName}
                          onChange={(e) => setEditCategoryName(e.target.value)}
                          className="min-w-0 flex-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                        />
                      ) : (
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-gray-800">{category.name}</p>
                          <p className="text-xs text-gray-400">
                            {categoryServiceCounts.get(category.id) ?? 0} услуг
                          </p>
                        </div>
                      )}

                      {editCategoryId === category.id ? (
                        <button
                          type="button"
                          onClick={() => saveEditedCategory(category.id)}
                          disabled={updateCategoryMutation.isPending || !editCategoryName.trim()}
                          className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white transition hover:opacity-90 disabled:opacity-50"
                        >
                          OK
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => startEditCategory(category)}
                          className="rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-100"
                        >
                          Изм.
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => deleteCategoryMutation.mutate(category.id)}
                        className="rounded-lg bg-white px-3 py-1.5 text-xs font-medium text-red-500 transition hover:bg-red-50"
                      >
                        Убрать
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="mb-5 min-w-0 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
        >
          <h3 className="font-semibold text-gray-800 mb-4">
            {editId ? "Редактировать услугу" : "Новая услуга"}
          </h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Название *</label>
              <input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                placeholder="Стрижка, маникюр..."
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Описание</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none"
                rows={2}
                placeholder="Кратко об услуге..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Длительность (мин) *</label>
                <input
                  required
                  type="number"
                  min={5}
                  max={480}
                  value={form.durationMin}
                  onChange={(e) => setForm({ ...form, durationMin: Number(e.target.value) })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Цена от (₽) *</label>
                <input
                  required
                  type="number"
                  min={0}
                  value={Math.floor(form.priceFrom / 100)}
                  onChange={(e) => setForm({ ...form, priceFrom: Number(e.target.value) * 100 })}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Цена до (₽, необязательно)</label>
              <input
                type="number"
                min={0}
                value={form.priceTo ? Math.floor(Number(form.priceTo) / 100) : ""}
                onChange={(e) =>
                  setForm({ ...form, priceTo: e.target.value ? String(Number(e.target.value) * 100) : "" })
                }
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            {(showPriceSectionControls || hasConfiguredPriceSections) && (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Раздел прайса</label>
                <select
                  value={form.categoryId}
                  onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                >
                  <option value="">Без раздела</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs leading-4 text-gray-400">
                  Раздел нужен только для длинного или разнопрофильного прайса. Если не выбрать раздел,
                  услуга останется в общем списке.
                </p>
              </div>
            )}

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="w-4 h-4 accent-primary"
              />
              <span className="text-sm text-gray-700">Активна (доступна для записи)</span>
            </label>
          </div>

          {error && <p className="text-red-500 text-sm mt-3">{error}</p>}

          <div className="flex gap-2 mt-4">
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="flex-1 bg-primary text-white py-2 rounded-xl font-medium text-sm hover:opacity-90 transition disabled:opacity-50"
            >
              {editId ? "Сохранить" : "Создать"}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition"
            >
              Отмена
            </button>
          </div>
        </form>
      )}

      {/* Список активных */}
      <div className="space-y-2">
        {active.map((s) => (
          <ServiceRow
            key={s.id}
            service={s}
            categoryName={s.categoryId ? categoryById.get(s.categoryId)?.name ?? null : null}
            onEdit={() => openEdit(s)}
            onToggle={() => toggleMutation.mutate({ id: s.id, isActive: false })}
          />
        ))}
      </div>

      {/* Неактивные */}
      {inactive.length > 0 && (
        <details className="mt-5">
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-600">
            Неактивные ({inactive.length})
          </summary>
          <div className="mt-2 space-y-2">
            {inactive.map((s) => (
              <ServiceRow
                key={s.id}
                service={s}
                categoryName={s.categoryId ? categoryById.get(s.categoryId)?.name ?? null : null}
                onEdit={() => openEdit(s)}
                onToggle={() => toggleMutation.mutate({ id: s.id, isActive: true })}
                onDelete={() => deleteMutation.mutate(s.id)}
              />
            ))}
          </div>
        </details>
      )}

      {services.length === 0 && (
        <div className="text-center py-14">
          <p className="text-4xl mb-3">💼</p>
          <p className="text-gray-500">Услуг ещё нет</p>
          <p className="text-sm text-gray-400 mt-1">Добавьте первую услугу</p>
        </div>
      )}
    </div>
  );
}

function ServiceRow({
  service: s,
  categoryName,
  onEdit,
  onToggle,
  onDelete,
}: {
  service: Service;
  categoryName?: string | null;
  onEdit: () => void;
  onToggle: () => void;
  onDelete?: () => void;
}) {
  return (
    <div className={`flex min-w-0 items-center gap-3 rounded-2xl border bg-white p-4 shadow-sm ${!s.isActive ? "opacity-50" : "border-gray-100"}`}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-gray-800 truncate">{s.name}</p>
        {categoryName && <p className="mt-1 text-xs font-medium text-gray-500">{categoryName}</p>}
        <p className="text-xs text-gray-400 mt-0.5">
          {s.durationMin} мин · {fmtPrice(s.priceFrom)}
          {s.priceTo && s.priceTo > s.priceFrom ? ` — ${fmtPrice(s.priceTo)}` : ""}
        </p>
      </div>
      <div className="flex max-w-[13rem] shrink-0 flex-wrap justify-end gap-2">
        <button
          type="button"
          onClick={onEdit}
          className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
        >
          Изм.
        </button>
        <button
          type="button"
          onClick={onToggle}
          className={`text-xs px-3 py-1.5 rounded-lg transition font-medium ${
            s.isActive
              ? "bg-red-50 text-red-500 hover:bg-red-100"
              : "bg-green-50 text-green-600 hover:bg-green-100"
          }`}
        >
          {s.isActive ? "Откл." : "Вкл."}
        </button>
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className="rounded-lg bg-red-50 px-3 py-1.5 text-xs font-medium text-red-500 transition hover:bg-red-100"
          >
            Удалить
          </button>
        )}
      </div>
    </div>
  );
}
